import { useTrigger } from '@lib/common';
import {
  Button,
  ListItemText,
  Dialog,
  ListItem,
  DialogContent,
  DialogTitle,
  ListItemAvatar,
  Divider,
  Grid,
  List,
  Typography,
  DialogActions,
  CircularProgress,
  DialogProps,
  Tooltip,
} from '@material-ui/core';
import * as mapService from '@api/map';
import { useEffect, useMemo } from 'react';
import SearchBox from '@components/search-box';
import { ColoredAvatar } from '@components/colored-elements';
import { grey, orange } from '@material-ui/core/colors';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import { LatLngTuple } from 'leaflet';
import { useState } from 'react';
import { catchError, tap } from 'rxjs/operators';
import { GenericLocationItem } from '@api/map/types';
import styled from 'styled-components';
import { thumbStack } from '@lib/common/css';
import { ImDrawer2 } from '@icon/react-icons';
import { forwardRef } from 'react';
import { LocationCard } from './location-card';

export interface LocationPickerProps extends DialogProps {
  onFinish?: (value: GenericLocationItem) => any;
  defaultLocation?: GenericLocationItem;
  cardTitle?: React.ReactNode;
}

export const LocationPicker = ({
  onFinish = null,
  defaultLocation = null,
  cardTitle = null,
  ...props
}: LocationPickerProps) => {
  const [activeItem, setActiveItem] = useState<GenericLocationItem>(defaultLocation);

  const { trigger: searchPlaces, loading, error, result } = useTrigger(
    (query: string) =>
      mapService.locationIqSearch(query).pipe(
        tap(r => (r[0] ? setActiveItem(r[0]) : null)),
        catchError(err =>
          [400, 404].includes(err?.response?.status)
            ? Promise.reject(`No Results found for '${err?.config?.params?.q}'`)
            : Promise.reject(`${err}`)
        )
      ),
    null,
    {},
    '',
    false
  );

  const markerPos = useMemo<LatLngTuple>(
    () =>
      activeItem && activeItem.lat && activeItem.lng ? [activeItem.lat, activeItem.lng] : null,
    [activeItem]
  );

  const borderStyle = { borderRight: '1px solid #b5b5b5' };

  const searchLimit = query => (query.length >= 2 ? searchPlaces(query) : null);

  const finish = evt => {
    onFinish && onFinish(activeItem);
    props.onClose && props.onClose(evt, null);
  };

  return (
    <Dialog keepMounted={false} {...props}>
      <DialogTitle>Pick a location</DialogTitle>
      <DialogContentThumbstack dividers style={{ background: '#f5f5f5' }}>
        <DialogContainer>
          <Grid container spacing={2}>
            <Grid item xs={3} style={borderStyle}>
              <SearchResultsContainer>
                <SearchBox label="Search For a place" onSearch={searchLimit} />
                {loading ? (
                  <EmptyResultsContainer>
                    <CircularProgress />
                  </EmptyResultsContainer>
                ) : !error && result && result.length ? (
                  <List dense>
                    {result.map((r, i) => (
                      <ListItem
                        selected={activeItem?.uuid === r.uuid}
                        key={i}
                        button
                        onClick={() => setActiveItem(r)}>
                        <Tooltip title={r.type.toUpperCase()}>
                          <ListItemAvatar>
                            <TypeIcon type={r.type} />
                          </ListItemAvatar>
                        </Tooltip>
                        <ListItemText primary={r.name} secondary={r.subHeader.slice(0, 50)} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <EmptyResult error={error} />
                )}
              </SearchResultsContainer>
            </Grid>
            <Grid item xs style={borderStyle}>
              {markerPos ? (
                <MapContainer
                  style={{ height: '100%', position: 'relative' }}
                  center={markerPos}
                  zoom={3}
                  scrollWheelZoom={false}>
                  <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MovingMarker position={markerPos} />
                </MapContainer>
              ) : (
                <EmptyResult error={error} forMap />
              )}
            </Grid>

            <Grid item xs={4}>
              <Typography variant="h6">Selected Location</Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Preview of the location card.
              </Typography>
              <Divider />
              <br />
              <LocationCard title={cardTitle} hideActions value={activeItem} />
            </Grid>
          </Grid>
        </DialogContainer>
      </DialogContentThumbstack>
      <DialogActions>
        <Button color="primary" onClick={props.onClose as any}>
          Cancel
        </Button>
        <Button color="primary" onClick={finish}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EmptyResult = ({ error, forMap = false }: { error: any; forMap?: boolean }) => (
  <EmptyResultsContainer>
    <span>
      <ImDrawer2 fontSize="large" style={{ color: grey[600] }} />
      <br />
      <Typography style={{ color: grey[600] }}>
        {error ? error : forMap ? 'No Location Selected' : 'No Data'}
      </Typography>
    </span>
  </EmptyResultsContainer>
);

const DialogContentThumbstack = styled(DialogContent)`
  ${thumbStack}
`;

const DialogContainer = styled.div`
  height: 65vh;
  ${thumbStack};
`;

const SearchResultsContainer = styled.div`
  height: 65vh;
  overflow: auto;
  ${thumbStack}
`;

const EmptyResultsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100% - 48px);

  span {
    text-align: center;
  }
`;

const TypeIcon = ({ type }: { type: string }) => (
  <ColoredAvatar colored={orange[800]}>{type.slice(0, 2).toUpperCase()}</ColoredAvatar>
);

const MovingMarker: typeof Marker = forwardRef(({ position, ...props }, ref) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(position);
  }, [position]);
  return <Marker ref={ref} position={position} {...props} />;
});
